import axios, { AxiosError } from 'axios'
import type {
  LoginResponse,
  User,
  ApiError,
  PortalRegisterParams,
  RegisterResponse,
} from '@/models/auth'

import { ENV } from '../env.js'

const API_URL = (ENV.backendUrl ?? 'http://localhost:4000') + '/api'

class AuthService {
  private setAuthHeader(token: string | null) {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete axios.defaults.headers.common['Authorization']
    }
  }

  async registerPortalUser(params: PortalRegisterParams): Promise<RegisterResponse> {
    try {
      const response = await axios.post<RegisterResponse>(`${API_URL}/portal/register`, {
        user: params,
      })

      this.setAuthHeader(response.data.token)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred' }
    }
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>(`${API_URL}/portal/login/`, {
        user: { email, password },
      })

      this.setAuthHeader(response.data.token)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred' }
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const response = await axios.post<{ user: User }>(`${API_URL}/portal/me`)
      return response.data.user
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred' }
    }
  }

  async logout(): Promise<void> {
    try {
      await axios.delete(`${API_URL}/portal/logout`)
      this.setAuthHeader(null)
      localStorage.clear()
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred' }
    }
  }

  setupAuth(token: string | null) {
    this.setAuthHeader(token)
  }
}

export default new AuthService()
